import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  AuthorizeLabelPrintResponse,
  LabelsService,
  SparePartLabelInfo
} from './labels.service';

export interface SparePartLabelPrintOptions {
  useSharedNumber?: boolean;
  includePackageLabel?: boolean;
  explicitWorkshopId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class LabelPrintAuthorizationService {
  constructor(private labelsService: LabelsService) {}

  printAuthorizedSparePartLabels(
    order: any,
    labels: SparePartLabelInfo[],
    options: SparePartLabelPrintOptions = {}
  ): Observable<void> {
    const sparePartIds = this.extractSparePartIds(labels);

    if (sparePartIds.length === 0) {
      this.labelsService.printLabelsWindow(
        order,
        labels,
        options.useSharedNumber ?? false,
        options.includePackageLabel ?? false,
        options.explicitWorkshopId ?? order?.workshopId
      );
      return of(undefined);
    }

    return this.authorizeAndPrint(order, labels, sparePartIds, options);
  }

  private authorizeAndPrint(
    order: any,
    labels: SparePartLabelInfo[],
    sparePartIds: number[],
    options: SparePartLabelPrintOptions,
    pin?: string
  ): Observable<void> {
    return this.labelsService.authorizeLabelPrint({ sparePartIds, pin }).pipe(
      switchMap((auth: AuthorizeLabelPrintResponse) => {
        if (auth.authorized) {
          this.labelsService.printLabelsWindow(
            order,
            labels,
            options.useSharedNumber ?? false,
            options.includePackageLabel ?? false,
            options.explicitWorkshopId ?? order?.workshopId
          );
          return this.labelsService.recordLabelPrint({ sparePartIds }).pipe(map(() => undefined));
        }

        if (auth.requiresPin) {
          if (pin) {
            return throwError(() => new Error(auth.message || 'PIN incorrecto.'));
          }

          return from(this.promptReprintPin(auth.reprintSparePartIds.length)).pipe(
            switchMap(enteredPin => {
              if (!enteredPin) {
                return throwError(() => new Error('Impresión cancelada.'));
              }
              return this.authorizeAndPrint(order, labels, sparePartIds, options, enteredPin);
            })
          );
        }

        return throwError(() => new Error(auth.message || 'No se pudo autorizar la impresión.'));
      })
    );
  }

  private extractSparePartIds(labels: SparePartLabelInfo[]): number[] {
    return [...new Set(labels.filter(label => label.sparePartId > 0).map(label => label.sparePartId))];
  }

  private promptReprintPin(reprintCount: number): Promise<string | null> {
    return new Promise(resolve => {
      const modalId = 'labelReprintPinModal';
      document.getElementById(modalId)?.remove();

      const modalHtml = `
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style="z-index: 10000;" id="${modalId}">
          <div class="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4" onclick="event.stopPropagation()">
            <div class="p-4 border-b">
              <h3 class="text-lg font-bold">PIN de reimpresión</h3>
            </div>
            <div class="p-4">
              <p class="text-sm text-gray-600 mb-4">
                ${reprintCount} repuesto(s) ya tienen etiqueta impresa. Ingrese el PIN de 4 dígitos para reimprimir.
              </p>
              <input
                id="labelReprintPinInput"
                type="password"
                inputmode="numeric"
                maxlength="4"
                pattern="[0-9]*"
                autocomplete="off"
                class="w-full border rounded px-3 py-2 text-center text-lg tracking-widest"
                placeholder="••••"
              />
              <p id="labelReprintPinError" class="text-sm text-red-600 mt-2 hidden"></p>
            </div>
            <div class="p-4 border-t flex justify-end space-x-2">
              <button type="button" id="labelReprintPinCancel" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Cancelar
              </button>
              <button type="button" id="labelReprintPinConfirm" class="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', modalHtml);

      const modal = document.getElementById(modalId);
      const input = document.getElementById('labelReprintPinInput') as HTMLInputElement | null;
      const errorEl = document.getElementById('labelReprintPinError');
      const cancelBtn = document.getElementById('labelReprintPinCancel');
      const confirmBtn = document.getElementById('labelReprintPinConfirm');

      const cleanup = (value: string | null) => {
        modal?.remove();
        resolve(value);
      };

      cancelBtn?.addEventListener('click', () => cleanup(null));

      const submit = () => {
        const pin = (input?.value || '').trim();
        if (!/^\d{4}$/.test(pin)) {
          if (errorEl) {
            errorEl.textContent = 'Ingrese un PIN de 4 dígitos.';
            errorEl.classList.remove('hidden');
          }
          return;
        }
        cleanup(pin);
      };

      confirmBtn?.addEventListener('click', submit);
      input?.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        }
        if (event.key === 'Escape') {
          cleanup(null);
        }
      });

      setTimeout(() => input?.focus(), 50);
    });
  }
}
